import UrlPattern from 'url-pattern';
import { ROUTE_404 } from '../../post-page/constants/routes';
import { type RedirectFn, type RouteResolverFn } from '../types/route-resolver-fn.type';

const FALLBACK_ROUTE = '*';
const MATCH_RETRY_COUNT = 5;

const RESULT_IDENTIFIER = '@@RouterResult';

export type RouteParams = Record<string, string>;

/** Per-route param shapes (e.g. `RouteCategoryParams`) mark optional segments. */
export type RouteParamsShape = Record<string, string | undefined>;

export type RouteOptions = {
  preFetch?: boolean;
};

/** The object handed to every route callback and to `onMatch` listeners. */
export type RouteObject = {
  pathname: string;
  encodedPathname: string;
  route: string;
  params: RouteParams;
  prevMatches: RouteObject[];
  [RESULT_IDENTIFIER]?: boolean;
};

/** Kept identical to `RouteResolverFn`'s third parameter so resolvers stay assignable. */
export type RouteResolveArg = Parameters<RouteResolverFn>[2];

/**
 * Route callbacks are the `RouteResolverFn`s built by `create-routers/*`, but
 * `add` also accepts plain side-effecting handlers (e.g. `ROUTE_LOGIN`), hence
 * the loose return type.
 */
export type RouteCallback<P extends RouteParamsShape = RouteParams> = (
  route: RouteObject & { params: P },
  redirect: RedirectFn,
  resolve: RouteResolveArg,
) => unknown;

/** Resolves an unmatched path to another path; may be sync (tests) or async. */
export type CustomRouteHandler = () =>
  | string
  | null
  | undefined
  | Promise<string | null | undefined>;

type RouteEntry = {
  callback: RouteCallback;
  options: RouteOptions;
};

/** `fallback()` stores the redirect target alongside the copied entry. */
type FallbackRouteEntry = RouteEntry & { route: string };

type MatchPathResult = {
  pathname: string;
  route: string;
  params: RouteParams;
  callback: RouteCallback;
  options?: RouteOptions;
};

type PreFetchEntry = MatchPathResult & {
  preFetchResult: Promise<unknown>;
};

const URL_PATTERN_OPTIONS = { segmentValueCharset: 'a-zA-Z0-9-_~ %.' };
const matchRoute = (route: string, path: string): RouteParams | null =>
  new UrlPattern(route, URL_PATTERN_OPTIONS).match(path);

const decodeParams = (match: Record<string, string>) =>
  Object.keys(match).reduce<Record<string, string>>((acc, cur) => {
    acc[cur] = decodeURIComponent(match[cur]);
    return acc;
  }, {});

const normalize = (pathname: string) => {
  if (pathname === '' || pathname === '/') {
    return '/';
  }
  return pathname.replace(/\/+$/g, '');
};

export class Router {
  static resultIdentifier = RESULT_IDENTIFIER;

  routes: Map<string, RouteEntry>;

  preFetchResult: Map<string, PreFetchEntry>;

  matchListeners: ((result: RouteObject) => void)[];

  customRouteHandler?: CustomRouteHandler;

  constructor() {
    this.routes = new Map();
    this.preFetchResult = new Map();
    this.matchListeners = [];
  }

  add = <P extends RouteParamsShape>(
    route: string,
    callback: RouteCallback<P>,
    options: RouteOptions = {},
  ) => {
    // Each route declares its own param shape, but the router only ever produces
    // the decoded `Record<string, string>` from the URL pattern.
    this.routes.set(route, { callback: callback as RouteCallback, options });
  };

  fallback = (redirect: string) => {
    this.routes.set(FALLBACK_ROUTE, {
      route: redirect,
      ...this.routes.get(redirect),
    } as FallbackRouteEntry);
  };

  onMatch = (callback: (result: RouteObject) => void) => this.matchListeners.push(callback);

  triggerMatch = (result: RouteObject) => this.matchListeners.forEach((cb) => cb(result));

  preFetch = async (path: string) => {
    const {
      route,
      pathname,
      params,
      callback,
      options = {},
    } = (await this.matchPath({ pathname: path })) || ({} as Partial<MatchPathResult>);
    if (route && callback && options.preFetch) {
      const preFetchResult = Promise.resolve(
        callback({ pathname, route, params } as RouteObject, () => {}, {
          preFetch: true,
          preFetchResult: undefined,
        }),
      );
      this.preFetchResult.set(path, {
        route,
        pathname: pathname!,
        params: params!,
        callback,
        preFetchResult,
      });
    }
  };

  resolve = (path: string) => {
    const preFetchRoute = this.preFetchResult.get(path);
    preFetchRoute && this.preFetchResult.delete(path);
    return this.match(path, [], preFetchRoute);
  };

  addCustomRouteHandler = (handler: CustomRouteHandler) => {
    this.customRouteHandler = handler;
  };

  match = async (
    path: string,
    prevMatches: RouteObject[] = [],
    preFetchRoute?: PreFetchEntry,
  ): Promise<RouteObject> => {
    if (prevMatches.length >= MATCH_RETRY_COUNT) {
      throw new Error('too many redirects');
    }

    const { route, pathname, params, callback, preFetchResult }: Partial<PreFetchEntry> =
      preFetchRoute || (await this.matchPath({ pathname: path, prevMatches })) || {};

    if (!route) {
      throw new Error('failed to match route');
    }

    const routeObj: RouteObject = {
      pathname: decodeURIComponent(pathname!),
      encodedPathname: pathname!,
      route,
      params: params!,
      prevMatches,
      [RESULT_IDENTIFIER]: true,
    };

    this.triggerMatch(routeObj);
    return this.resolveRoute(routeObj, callback, prevMatches, preFetchResult);
  };

  matchPath = async ({
    pathname,
    matchCustomRoute = true,
    prevMatches = [],
  }: {
    pathname: string;
    matchCustomRoute?: boolean;
    prevMatches?: RouteObject[];
  }): Promise<MatchPathResult | undefined> => {
    const normalizedPathname = normalize(pathname);

    for (let [route, { callback, options }] of this.routes) {
      const match = matchRoute(route, normalizedPathname);

      const isLastRoute = route === FALLBACK_ROUTE || route === ROUTE_404;

      if (
        matchCustomRoute &&
        this.customRouteHandler &&
        isLastRoute &&
        prevMatches.length < MATCH_RETRY_COUNT - 1
      ) {
        const customRoute = await this.customRouteHandler();
        if (customRoute) {
          return this.matchPath({
            pathname: customRoute,
            matchCustomRoute: false,
          });
        }
      }

      if (match) {
        if (route === FALLBACK_ROUTE) {
          ({ route, callback, options } = this.routes.get(FALLBACK_ROUTE) as FallbackRouteEntry);
        }

        return {
          pathname,
          route,
          params: decodeParams(match),
          callback,
          options,
        };
      }
    }
  };

  resolveRoute = async (
    routeObj: RouteObject,
    callback: RouteCallback | undefined,
    prevMatches: RouteObject[] = [],
    preFetchResult?: unknown,
  ): Promise<RouteObject> => {
    if (!callback) {
      return routeObj;
    }
    const redirect: RedirectFn = (path) => this.match(path, prevMatches.concat(routeObj));
    const val = await Promise.resolve(
      callback(routeObj, redirect, { preFetch: undefined, preFetchResult }),
    );
    return val && (val as RouteObject)[RESULT_IDENTIFIER] ? (val as RouteObject) : routeObj;
  };
}
